import Cookies from 'js-cookie';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

class UserManager {
  constructor() {
    this.routes = {
      login: '/api/v1/user/login',
      logout: '/api/v1/user/logout',
      verifyAccess: '/api/v1/user/verifyAccess',
      setPassword: '/api/v1/user/setPassword',
      userDetails: '/api/v1/user/details',
      resetPassword: '/api/v1/email/resetPassword',
      createOrder: '/api/v1/order/create',
      fileLog: '/api/v1/file/log',
      agreeToTerms: '/api/v1/user/agreeToTerms',
    };

    this.apiUrl = process.env.API_URL;
    this.xapiKey = process.env.X_API_KEY;
    this.sessionTTL = 90;

    if (this.isLoggedIn()) this.verifyAccess();
  }

  login({ email, password }) {
    const endpoint = this.apiUrl + this.routes.login;
    const data = { email, password };
    const options = {
      headers: { 'x-api-key': this.xapiKey, 'X-Origin': window.origin },
    };
    return axios.post(endpoint, data, options)
      .then((response) => {
        if (response.status === 200 && response.data && response.data.token) {
          this.storeCredentials(response.data.token, email);

          const url = this.addQueryParam(window.location.href, 'ts', Date.now());
          window.history.replaceState({}, document.title, url);
          window.location.reload();

          return { success: true };
        }
        return { error: true };
      })
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return { error: true };
      });
  }

  logout() {
    const endpoint = this.apiUrl + this.routes.logout;
    const options = {
      headers: { Authorization: `Bearer ${this.getToken()}`, 'x-api-key': this.xapiKey },
    };
    return axios.post(endpoint, {}, options)
      .then((response) => {
        if (response.status === 200) {
          this.removeCredentials();
          const url = this.addQueryParam(window.location.href, 'ts', Date.now());
          window.history.replaceState({}, document.title, url);
          window.location.reload();
        }
      })
      .catch(error => console.log('Error on logout: ', error.response));
  }

  verifyAccess() {
    const endpoint = this.apiUrl + this.routes.verifyAccess;
    const options = {
      headers: { Authorization: `Bearer ${this.getToken()}`, 'x-api-key': this.xapiKey, 'X-Origin': window.origin },
    };
    const data = { page_url: window.location.href };
    return axios.post(endpoint, data, options)
      .then(() => {}).catch(() => {
        // If user has an invalid token or something else is wrong - log out
        this.logout();
      });
  }

  setPassword({ password, token }) {
    const endpoint = this.apiUrl + this.routes.setPassword;
    const data = { token, password };
    const options = {
      headers: { 'x-api-key': this.xapiKey, 'X-Origin': window.origin },
    };
    return axios.post(endpoint, data, options)
      .then((response) => {
        if (
          response.status === 200
          && response.data
          && response.data.token
          && response.data.email
        ) {
          // Login user on success
          this.storeCredentials(response.data.token, response.data.email);
          window.sessionStorage.setItem('new_user_password', true);
          const url = this.addQueryParam(window.location.href, 'ts', Date.now());
          window.history.replaceState({}, document.title, url);
          window.location.reload();

          return { success: true };
        }
        return { error: true };
      })
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return { error: true };
      });
  }

  resetPassword({ email, url }) {
    const endpoint = this.apiUrl + this.routes.resetPassword;
    const data = { email, url };
    const options = {
      headers: { 'x-api-key': this.xapiKey, 'X-Origin': window.origin },
    };
    return axios.post(endpoint, data, options)
      .then((response) => {
        if (
          response.status === 200
          && response.data
        ) {
          return { success: true };
        }
        return { error: true };
      })
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return { error: true };
      });
  }

  isLoggedIn() {
    const hasUserName = Cookies.get('login-username') || false;
    const hasToken = Cookies.get('login-token') || false;

    return hasUserName && hasToken;
  }

  getToken() {
    return Cookies.get('login-token') || null;
  }

  getUserPartnerGroups() {
    const token = this.getToken();
    if (token) {
      const userDetails = jwtDecode(token);
      return { partnerGroups: userDetails.partner_groups };
    }
    return false;
  }

  getUserDetails() {
    const endpoint = this.apiUrl + this.routes.userDetails;
    const options = {
      headers: { Authorization: `Bearer ${this.getToken()}`, 'x-api-key': this.xapiKey },
    };
    return axios.get(endpoint, options)
      .then(response => response.data).catch((error) => {
        console.error('Error getting user details: ', error.response);
      });
  }

  createOrder(data) {
    const endpoint = this.apiUrl + this.routes.createOrder;
    const options = {
      headers: { Authorization: `Bearer ${this.getToken()}`, 'x-api-key': this.xapiKey },
    };
    return axios.post(endpoint, { order: JSON.stringify(data) }, options)
      .then(response => response.data).catch((error) => {
        console.error('Error creating order: ', error.response);
      });
  }

  sendFileEvent(data) {
    const endpoint = this.apiUrl + this.routes.fileLog;
    const token = this.getToken();
    if (token) {
      const options = {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': this.xapiKey },
      };
      axios.post(endpoint, data, options)
        .then(() => {})
        .catch(() => {});
    }
  }

  agreeToTerms(data) {
    const endpoint = this.apiUrl + this.routes.agreeToTerms;
    const token = this.getToken();
    if (token) {
      const options = {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': this.xapiKey },
      };
      axios.post(endpoint, data, options)
        .then(res => res)
        .catch(err => err);
    }
  }

  storeCredentials(token, username) {
    if (!token) return;
    Cookies.set('login-username', username, { expires: this.sessionTTL });
    Cookies.set('login-token', token, { expires: this.sessionTTL });
  }

  removeCredentials() {
    Cookies.remove('login-username');
    Cookies.remove('login-token');
  }

  addQueryParam(url, param, value) {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    params.set(param, value);
    return `${window.location.href.split('?')[0]}?${params.toString()}`;
  }
}


const User = new UserManager();
export default User;
